import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BsThreeDotsVertical, BsPlus } from "react-icons/bs";
import {
  FaClipboardCheck,
  FaEye,
  FaBell,
  FaGraduationCap,
  FaBook,
  FaCalendarCheck,
} from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Container, Spinner } from "react-bootstrap";
import SportsIMg from "../../assets/img/sports.jpg";
import { getNotice } from "../../store/slices/notice";
import { getHoliday } from "../../store/slices/holiday";
import { useNavigate } from "react-router-dom";

export default function TeacherHome() {
  const dispatch = useDispatch();
  const [calendarDate, setCalendarDate] = useState(new Date());
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.auth);

  const teacherName =
    profile?.teacherId?.name ||
    profile?.name ||
    "Teacher";

  const quickAccess = [
    { label: "Homework", icon: FaBook, path: "/teacher/homework" },
    { label: "Attendance", icon: FaClipboardCheck, path: "/teacher/add-attendance" },
    { label: "Notice", icon: FaBell, path: "/teacher/notices" },
    { label: "Holiday", icon: FaCalendarCheck, path: "/teacher/holidays" },
  ];
  const {
    noticeList: notices = [],
    loading: noticeLoading,
    error: noticeError,
  } = useSelector((state) => state.notice || {});

  const {
    holidayList: holidays = [],
    loading: holidayLoading,
    error: holidayError,
  } = useSelector((state) => state.holiday || {});

  useEffect(() => {
    dispatch(getNotice());
    dispatch(getHoliday());
  }, [dispatch]);

  // Fast lookup: "YYYY-MM-DD" -> holiday item, used to mark calendar tiles.
  const holidayMap = React.useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      if (!h?.date) return;
      const key = new Date(h.date).toDateString();
      map[key] = h;
    });
    return map;
  }, [holidays]);

  const isHoliday = (date) => Boolean(holidayMap[date.toDateString()]);

  return (
    <>


      <div className="homepage-container th-root">
        {/* HERO — mirrors the app's "Welcome back, Shishir" banner */}
        <div className="th-hero">
          <div className="th-hero-row">
            <div>
              <p className="th-hero-sub">Welcome back,</p>
              <h1 className="th-serif th-hero-name">{teacherName}</h1>
              <div className="th-hero-pill">
                <FaGraduationCap /> Teacher · Octal
              </div>
            </div>

          </div>
        </div>

        <Container className="dh-container" style={{ position: "relative" }}>
          {/* Quick Access */}
          <div className="th-section-title mt-5">
            <span className="th-section-bar" />
            <h2>Quick Access</h2>
          </div>

          <div className="th-quick-wrapper">
            {quickAccess.map((item) => (
              <button
                className="th-quick-card"
                key={item.label}
                onClick={() => navigate(item.path)}
              >
                <div className="th-quick-icon">
                  <item.icon />
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="dh-bottom">
            {/* Notice Board — from API */}
            <div className="nb-card">
              <div className="nb-header">
                <h4>Notice Board</h4>
                <button className="nb-add-btn">
                  <BsPlus />
                </button>
              </div>

              {noticeLoading ? (
                <div className="nb-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading notices…
                </div>
              ) : noticeError ? (
                <div className="nb-error">{noticeError || "Couldn't load notices."}</div>
              ) : notices.length === 0 ? (
                <div className="nb-empty">No notices yet.</div>
              ) : (
                <div className="nb-list">
                  {notices.map((item) => (
                    <div className="nb-item" key={item.id ?? item._id}>
                      <img
                        src={item.image || SportsIMg}
                        alt="notice"
                        className="nb-thumb"
                      />

                      <div className="nb-content">
                        <p className="nb-title">{item.title}</p>
                      </div>
                      <div className="nb-daterow d-flex">
                        <span className={`nb-date ${item.highlight ? "active" : ""}`}>
                          {item.date
                            ? new Date(item.date).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : ""}
                        </span>

                        {item.views != null && (
                          <div className="nb-views">
                            <span className="nb-eye">
                              <FaEye />
                            </span>
                            <span>{item.views}</span>
                          </div>
                        )}

                        <button className="nb-more">
                          <BsThreeDotsVertical />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Event Calendar — holidays from API */}
            <div className="dh-card dh-calendar-card">
              <div className="dh-calendar-head">
                <h4>Event Calendar</h4>
                <span className="dh-calendar-more">•••</span>
              </div>

              {holidayLoading ? (
                <div className="nb-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading holidays…
                </div>
              ) : holidayError ? (
                <div className="nb-error">{holidayError || "Couldn't load holidays."}</div>
              ) : (
                <>
                  <Calendar
                    onChange={setCalendarDate}
                    value={calendarDate}
                    showNeighboringMonth={false}
                    next2Label={null}
                    prev2Label={null}
                    formatShortWeekday={(locale, date) =>
                      date.toLocaleDateString(locale, { weekday: "short" })
                    }
                    tileClassName={({ date, view }) =>
                      view === "month" && isHoliday(date) ? "th-holiday-tile" : null
                    }
                    tileContent={({ date, view }) =>
                      view === "month" && isHoliday(date) ? (
                        <span className="th-holiday-dot" />
                      ) : null
                    }
                  />

                  {holidays.length > 0 && (
                    <div className="th-holiday-legend">
                      <span className="th-holiday-legend-dot" />
                      Holiday
                    </div>
                  )}

                  {holidays.length > 0 && (
                    <div className="mt-3">
                      {holidays.slice(0, 4).map((h) => (
                        <div className="th-upcoming-holiday" key={h.id ?? h._id}>
                          <span className="th-upcoming-holiday-title">{h.title}</span>
                          <span className="th-upcoming-holiday-date">
                            {h.date
                              ? new Date(h.date).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                              : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}